import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { genNameInitials } from "@/utils/name";
import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

export interface Employee {
  id?: string;
  name?: string;
  avatarUrl?: string;
  workerNumber?: string;
}

type Props = CFormItemProps & {
  value?: Employee | null;
  onChange?: (value?: Employee | null) => void;
  /** 空の場合の表示テキスト */
  placeholder?: string;
};

/**
 * Card-based Form / EmployeeItem
 * 従業員情報（アバター、名前、従業員番号）を表示するフォームアイテム
 */
export const CFormEmployeeItem = buildCFormItem((props: Props) => {
  const { value, placeholder = "-", ...restProps } = props;

  const renderEmployeeDisplay = (employee: Employee | null) => {
    if (!employee || !employee.name) {
      return (
        <Text variant="p" className="text-neutral-muted-on-fill">
          {placeholder}
        </Text>
      );
    }

    return (
      <HStack className="items-center gap-1">
        <Avatar>
          <AvatarImage src={employee.avatarUrl} alt={employee.name || ""} />
          <AvatarFallback>
            {employee.name ? genNameInitials(employee.name) : ""}
          </AvatarFallback>
        </Avatar>
        <VStack className="gap-1">
          <HStack className="items-center gap-2">
            <Text variant="labelSemiLarge" className="text-neutral-on-fill">
              {employee.name}
              {employee.workerNumber && ` / ${employee.workerNumber}`}
            </Text>
          </HStack>
        </VStack>
      </HStack>
    );
  };

  return (
    <CFormItemBase
      {...restProps}
      readComponent={renderEmployeeDisplay(value ?? null)}
      writeComponent={renderEmployeeDisplay(value ?? null)}
    />
  );
});
